import { useCallback, useEffect, useRef, useState } from "react";
import { Scissors, Play } from "lucide-react";
import type { CnrCaseBlock, PositionedFlag, RoomTimelineData } from "../helpers/focusViewTypes";
import {
  annotateSegments,
  buildAxisLabels,
  flagPipPct,
  formatSelectedRange,
  getLiveBarPct,
} from "../helpers/focusViewHelpers";

const SEG_BG: Record<string, string> = {
  l: "#22C55E",
  f: "#EF4444",
  "f-missed": "#F59E0B",
  r: "#D1D5DB",
  rc: "#A78BFA",
};
const SEG_TEXT: Record<string, string> = {
  l: "#166534",
  f: "#fff",
  "f-missed": "#fff",
  r: "#6B7280",
  rc: "#4C1D95",
};

interface FocusTimelineProps {
  tlData: RoomTimelineData | null;
  roomFlags: PositionedFlag[]; // unresolved flags for this room (active + missed)
  allFlagsForRoom: PositionedFlag[]; // all flags incl. resolved, for the pip row
  nowMin: number;
  isLiveRoom: boolean;
  onSelectionPreview: (label: string) => void;
}

export function FocusTimeline({
  tlData,
  roomFlags,
  allFlagsForRoom,
  nowMin,
  isLiveRoom,
  onSelectionPreview,
}: Readonly<FocusTimelineProps>) {
  const barRef = useRef<HTMLDivElement | null>(null);
  const [selMode, setSelMode] = useState(false);
  const [selDragging, setSelDragging] = useState(false);
  const [selStart, setSelStart] = useState(0);
  const [selEnd, setSelEnd] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  const liveBarPct = isLiveRoom ? getLiveBarPct(nowMin) : 0;
  const livePosFrac = Math.max(0, (nowMin - 10) / 480);

  const axisHTML = buildAxisLabels(nowMin);

  const annotated = tlData
    ? annotateSegments(tlData.segs, liveBarPct)
    : [{ t: "r" as const, f: 1, lb: "No timeline data", cls: "r" }];

  // upcoming flag chips: flags between live edge and "now" — these sit in the open edit window
  const upcomingChips = roomFlags
    .map((f) => ({ f, pct: flagPipPct(f, nowMin) }))
    .filter(({ pct }) => pct >= liveBarPct && pct <= 100);

  const pips = allFlagsForRoom.map((f) => {
    const pct = flagPipPct(f, nowMin);
    const color = f.rv ? "#2563EB" : f.tlPos < livePosFrac ? "#F59E0B" : "#EF4444";
    return { f, pct, color };
  });

  const cnrBlocks: CnrCaseBlock[] = tlData?.cnr ?? [];

  const resetSelection = useCallback(() => {
    setSelStart(0);
    setSelEnd(0);
    setShowPreview(false);
  }, []);

  const toggleSelMode = useCallback(() => {
    setSelMode((prev) => {
      const next = !prev;
      if (!next) resetSelection();
      return next;
    });
  }, [resetSelection]);

  const handleBarMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!selMode || !barRef.current) return;
      const rect = barRef.current.getBoundingClientRect();
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      setSelStart(frac);
      setSelEnd(frac);
      setSelDragging(true);
      setShowPreview(false);
    },
    [selMode]
  );

  useEffect(() => {
    if (!selDragging) return;

    const handleMove = (e: MouseEvent) => {
      if (!barRef.current) return;
      const rect = barRef.current.getBoundingClientRect();
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      setSelEnd(frac);
    };

    const handleUp = () => {
      setSelDragging(false);
      setShowPreview((prevShow) => prevShow || Math.abs(selEnd - selStart) > 0.01);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selDragging, selStart, selEnd]);

  const selWidth = Math.abs(selEnd - selStart) * 100;
  const selLeft = Math.min(selStart, selEnd) * 100;
  const hasSelection = selWidth >= 0.5;

  const handlePreview = () => {
    onSelectionPreview(formatSelectedRange(selStart, selEnd, nowMin));
  };

  return (
    <div className="fv-section-body">
      <div className="flex justify-between text-[10px] text-gray-400 mb-[3px]">
        {axisHTML.map((t, i) => (
          <span key={`${t}-${i}`}>{t}</span>
        ))}
      </div>

      <div className="relative select-none mb-0.5" style={{ paddingTop: isLiveRoom ? 22 : 0 }}>
        {/* upcoming flag chips above the bar */}
        {isLiveRoom &&
          upcomingChips.map(({ f, pct }) => (
            <div
              key={f.id}
              className="absolute top-0 h-5 flex flex-col items-center pointer-events-none z-[6]"
              style={{ left: `${pct.toFixed(1)}%`, transform: "translateX(-50%)" }}
            >
              <span className="text-[7px] font-bold px-1 py-px rounded-[3px] bg-red-600 text-white whitespace-nowrap leading-[14px]">
                {f.tp === "pii" ? "PII" : "CDT"}
              </span>
              <span
                className="block w-0 h-0"
                style={{
                  borderLeft: "3px solid transparent",
                  borderRight: "3px solid transparent",
                  borderTop: "4px solid #DC2626",
                }}
              />
            </div>
          ))}

        <div
          ref={barRef}
          onMouseDown={handleBarMouseDown}
          className="flex h-7 rounded gap-px bg-gray-100 overflow-hidden"
          style={{ cursor: selMode ? "crosshair" : "default" }}
        >
          {annotated.map((s, i) =>
            s.t === "N" ? (
              <div key={`now-${i}`} className="w-0.5 rounded-sm bg-blue-600 shrink-0" />
            ) : (
              <div
                key={`${s.t}-${i}`}
                title={s.ti}
                className="h-full text-[9px] font-medium flex items-center justify-center overflow-hidden whitespace-nowrap"
                style={{
                  flex: s.f,
                  background: SEG_BG[s.cls] ?? SEG_BG.r,
                  color: SEG_TEXT[s.cls] ?? SEG_TEXT.r,
                }}
              >
                {s.lb ?? ""}
              </div>
            )
          )}
        </div>

        {/* flag pips */}
        <div
          className="absolute left-0 right-0 h-7 pointer-events-none z-[3]"
          style={{ top: isLiveRoom ? 22 : 0 }}
        >
          {pips.map(({ f, pct, color }) => (
            <div
              key={f.id}
              title={`${f.rv ? "✓ Resolved · " : ""}${f.tg} · ${f.tm}`}
              className="absolute top-px bottom-px w-[3px] rounded-sm pointer-events-auto cursor-pointer"
              style={{ left: `${pct.toFixed(1)}%`, background: color }}
            />
          ))}
        </div>

        {/* selection range overlay */}
        {hasSelection && (
          <div
            className="absolute h-7 bg-blue-600/[0.18] border-[1.5px] border-blue-600/65 rounded-sm pointer-events-none"
            style={{ top: isLiveRoom ? 22 : 0, left: `${selLeft}%`, width: `${selWidth}%` }}
          />
        )}

        {/* edit zone (open window between live edge and now) */}
        {isLiveRoom && (
          <div
            className="absolute h-7 bg-red-600/[0.06] pointer-events-none z-[1]"
            style={{
              top: 22,
              left: `${liveBarPct}%`,
              width: `${(100 - liveBarPct).toFixed(1)}%`,
              borderLeft: "1px dashed rgba(220,38,38,.3)",
            }}
          />
        )}

        {/* live marker */}
        {isLiveRoom && (
          <div
            className="absolute h-7 w-0.5 z-[5] pointer-events-none rounded-sm"
            style={{
              top: 22,
              left: `${liveBarPct}%`,
              background: "#fff",
              boxShadow: "0 0 0 1px #16A34A, 0 0 5px rgba(34,197,94,.7)",
            }}
          >
            <span
              className="absolute left-1/2 whitespace-nowrap text-[7px] font-bold tracking-wide rounded-[3px] px-1"
              style={{
                top: "calc(100% + 3px)",
                transform: "translateX(-50%)",
                color: "#16A34A",
                background: "rgba(34,197,94,.1)",
              }}
            >
              LIVE
            </span>
          </div>
        )}
      </div>

      {hasSelection && (
        <div className="text-[11px] text-blue-600 font-medium mt-[7px]">
          {formatSelectedRange(selStart, selEnd, nowMin)}
        </div>
      )}

      {/* CNR case bar */}
      {cnrBlocks.length > 0 && (
        <div className="relative h-4 mt-[5px] rounded-sm overflow-hidden">
          {cnrBlocks.map((c, i) => {
            const sp = (c.s / nowMin) * 100;
            const ep = c.e < 0 ? 100 : Math.min(100, (c.e / nowMin) * 100);
            const wp = Math.max(0, ep - sp);
            if (wp < 0.5) return null;
            return (
              <div
                key={`${c.lb}-${i}`}
                className="absolute top-0 h-full flex items-center px-1.5 overflow-hidden border-r border-black/20"
                style={{ left: `${sp}%`, width: `${wp}%`, background: c.col }}
              >
                <span className="text-[8px] font-semibold text-white/85 whitespace-nowrap overflow-hidden text-ellipsis w-full">
                  {c.lb}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between mt-2">
        <div className="fv-tl-tools ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={toggleSelMode}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
              selMode
                ? "bg-blue-50 text-blue-700 border-blue-600"
                : "bg-transparent text-gray-500 border-gray-300 hover:bg-gray-100"
            }`}
          >
            <Scissors size={13} /> Select range
          </button>
          {showPreview && (
            <button
              type="button"
              onClick={handlePreview}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border border-gray-300 text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <Play size={13} /> Preview
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
